import { useEffect, useState } from "react";
import { httpService } from "../services/httpService";

/**
 * Fetches prefill data from a submitted consent form
 * for a given orderId and refId.
 *
 * Best-effort: fetching prefill data should never block
 * the Boarding drawer from opening.
 */
export const useDrawerPrefill = ({ open, orderId, refId }) => {
  const [prefill, setPrefill] = useState(null);
  const [sourceForm, setSourceForm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Do not fetch when the drawer is closed or IDs are missing.
    if (!open || !orderId || !refId) {
      setPrefill(null);
      setSourceForm(null);
      setLoading(false);
      return;
    }

    // Each effect execution owns its request.
    // This also handles React Strict Mode effect cleanup correctly.
    let cancelled = false;

    const fetchPrefill = async () => {
      setLoading(true);

      try {
        console.log("[useDrawerPrefill] fetching:", {
          orderId,
          refId,
        });

        const res = await httpService.get(
          `/consentForm/drawer-prefill/${orderId}/${refId}`
        );

        /*
         * Support either response format:
         *
         * Axios-style:
         * res.data = { status, code, data: { prefill, ... } }
         *
         * Already-unwrapped:
         * res.data = { prefill, source_form_id, ... }
         */
        const responseData = res?.data ?? res;
        const payload = responseData?.data ?? responseData;

        console.log("[useDrawerPrefill] payload:", payload);

        if (cancelled) return;

        if (payload?.status === false) {
          setPrefill(null);
          setSourceForm(null);
          return;
        }

        setPrefill(payload?.prefill ?? null);

        setSourceForm(
          payload?.source_form_id
            ? {
                form_id: payload.source_form_id,
                submitted_at: payload.submitted_at ?? null,
              }
            : null
        );

        console.log(
          "[useDrawerPrefill] prefill loaded:",
          payload?.prefill ?? null
        );
      } catch (err) {
        if (!cancelled) {
          console.error(
            "[useDrawerPrefill] fetch failed:",
            err
          );

          setPrefill(null);
          setSourceForm(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPrefill();

    return () => {
      // Ignore results from an obsolete effect/request.
      cancelled = true;
    };
  }, [open, orderId, refId]);

  /**
   * Clears prefill data when needed.
   * A subsequent effect can fetch it again.
   */
  const clear = () => {
    setPrefill(null);
    setSourceForm(null);
  };

  return {
    prefill,
    sourceForm,
    loading,
    clear,
  };
};

export default useDrawerPrefill;